import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  useColorScheme,
  TextInput,
  Modal,
  Image,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const FoodInputHomeScreen = ({ navigation }) => {
  const isDarkMode = useColorScheme() === 'dark';
  const [searchText, setSearchText] = useState('');
  const [recognizedFood, setRecognizedFood] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [selectedInputMethod, setSelectedInputMethod] = useState(null);

  // 模拟识别结果
  const mockFoodData = {
    name: '红烧牛肉面',
    weight: 350,
    calories: 520,
    protein: 18.5,
    fat: 12.2,
    carbs: 78.6
  };

  // 模拟识别过程
  const simulateRecognition = (method) => {
    setSelectedInputMethod(method);
    
    if (method === 'voice') {
      setIsRecording(true);
      setTimeout(() => {
        setIsRecording(false);
        setRecognizedFood(mockFoodData);
      }, 2000);
    } else if (method === 'image') {
      setIsProcessingImage(true);
      setTimeout(() => {
        setIsProcessingImage(false);
        setRecognizedFood(mockFoodData);
      }, 2000);
    } else if (method === 'text' && searchText.trim()) {
      setRecognizedFood(mockFoodData);
    }
  };

  const handleSearch = () => {
    if (searchText.trim()) {
      simulateRecognition('text');
    }
  };

  const handleConfirm = () => {
    if (recognizedFood) {
      navigation.navigate('FoodInputConfirm', { foodData: recognizedFood });
    }
  };

  const resetRecognition = () => {
    setRecognizedFood(null);
    setSelectedInputMethod(null);
  };

  return (
    <ScrollView 
      style={{
        flex: 1,
        backgroundColor: isDarkMode ? '#8fa0c1' : '#ffffff',
      }}
      contentContainerStyle={{ padding: 16 }}
    >
      <View style={{ 
        padding: 20, 
        borderRadius: 16, 
        backgroundColor: isDarkMode ? '#a9b6d0' : '#f0f9ff',
        marginBottom: 20,
      }}>
        <Text style={{ 
          fontSize: 22, 
          fontWeight: 'bold', 
          color: isDarkMode ? 'white' : '#1e3a8a',
          marginBottom: 8,
        }}>
          智能食物录入
        </Text>
        <Text style={{ 
          fontSize: 15, 
          color: isDarkMode ? '#e0e7ff' : '#3b82f6',
        }}>
          选择录入方式，快速记录您的食物信息
        </Text>
      </View>

      {/* 多元化录入入口 */}
      <Text style={{
        fontSize: 18,
        fontWeight: 'bold',
        marginBottom: 12,
        color: isDarkMode ? 'white' : '#1f2937',
      }}>
        选择录入方式
      </Text>

      <View style={{
        flexDirection: 'row',
        marginBottom: 24,
        justifyContent: 'space-between',
      }}>
        {/* 语音识别录入 */}
        <TouchableOpacity
          style={{
            flex: 1,
            marginRight: 8,
            padding: 16,
            borderRadius: 16,
            backgroundColor: selectedInputMethod === 'voice' 
              ? (isDarkMode ? '#4c51bf' : '#4f46e5') 
              : (isDarkMode ? '#a9b6d0' : 'white'),
            alignItems: 'center',
            justifyContent: 'center',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 3,
          }}
          onPress={() => simulateRecognition('voice')}
        >
          <View style={{
            width: 56,
            height: 56,
            borderRadius: 28,
            backgroundColor: '#8b5cf6',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 8,
          }}>
            <Ionicons 
              name={isRecording ? "mic" : "mic-outline"} 
              size={28} 
              color="white" 
            />
          </View>
          <Text style={{
            fontSize: 16,
            fontWeight: '500',
            color: selectedInputMethod === 'voice' 
              ? 'white' 
              : (isDarkMode ? 'white' : '#1f2937'),
          }}>语音识别</Text>
          <Text style={{
            marginTop: 4,
            fontSize: 13,
            textAlign: 'center',
            color: selectedInputMethod === 'voice' 
              ? '#e0e7ff' 
              : (isDarkMode ? '#e0e0e0' : '#6b7280'),
          }}>
            {isRecording ? '正在录音...' : '一键语音识别食物'}
          </Text>
        </TouchableOpacity>

        {/* 图片识别录入 */}
        <TouchableOpacity
          style={{
            flex: 1,
            marginHorizontal: 8,
            padding: 16,
            borderRadius: 16,
            backgroundColor: selectedInputMethod === 'image' 
              ? (isDarkMode ? '#4c51bf' : '#4f46e5') 
              : (isDarkMode ? '#a9b6d0' : 'white'),
            alignItems: 'center',
            justifyContent: 'center',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 3,
          }}
          onPress={() => simulateRecognition('image')}
        >
          <View style={{
            width: 56,
            height: 56,
            borderRadius: 28,
            backgroundColor: '#0891b2',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 8,
          }}>
            <Ionicons 
              name={isProcessingImage ? "camera" : "camera-outline"} 
              size={28} 
              color="white" 
            />
          </View>
          <Text style={{
            fontSize: 16,
            fontWeight: '500',
            color: selectedInputMethod === 'image' 
              ? 'white' 
              : (isDarkMode ? 'white' : '#1f2937'),
          }}>图片识别</Text>
          <Text style={{
            marginTop: 4,
            fontSize: 13,
            textAlign: 'center',
            color: selectedInputMethod === 'image' 
              ? '#e0e7ff' 
              : (isDarkMode ? '#e0e0e0' : '#6b7280'),
          }}>
            {isProcessingImage ? '正在处理...' : '拍照或相册选取'}
          </Text>
        </TouchableOpacity>

        {/* 手动文字录入 */}
        <TouchableOpacity
          style={{
            flex: 1,
            marginLeft: 8,
            padding: 16,
            borderRadius: 16,
            backgroundColor: selectedInputMethod === 'text' 
              ? (isDarkMode ? '#4c51bf' : '#4f46e5') 
              : (isDarkMode ? '#a9b6d0' : 'white'),
            alignItems: 'center',
            justifyContent: 'center',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 3,
          }}
          onPress={() => setSelectedInputMethod('text')}
        >
          <View style={{
            width: 56,
            height: 56,
            borderRadius: 28,
            backgroundColor: '#ec4899',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 8,
          }}>
            <Ionicons name="search-outline" size={28} color="white" />
          </View>
          <Text style={{
            fontSize: 16,
            fontWeight: '500',
            color: selectedInputMethod === 'text' 
              ? 'white' 
              : (isDarkMode ? 'white' : '#1f2937'),
          }}>文字搜索</Text>
          <Text style={{
            marginTop: 4,
            fontSize: 13,
            textAlign: 'center',
            color: selectedInputMethod === 'text' 
              ? '#e0e7ff' 
              : (isDarkMode ? '#e0e0e0' : '#6b7280'),
          }}>
            手动输入食物名称
          </Text>
        </TouchableOpacity>
      </View>

      {/* 文字搜索框 */}
      {selectedInputMethod === 'text' && (
        <View style={{
          flexDirection: 'row',
          marginBottom: 24,
          backgroundColor: isDarkMode ? '#a9b6d0' : '#f3f4f6',
          borderRadius: 12,
          padding: 4,
          alignItems: 'center',
        }}>
          <TextInput
            style={{
              flex: 1,
              paddingVertical: 12,
              paddingHorizontal: 16,
              fontSize: 16,
              color: isDarkMode ? 'white' : '#1f2937',
            }}
            placeholder="输入食物名称（例如：红烧牛肉面）"
            placeholderTextColor={isDarkMode ? '#e0e0e0' : '#9ca3af'}
            value={searchText}
            onChangeText={setSearchText}
            returnKeyType="search"
            onSubmitEditing={handleSearch}
          />
          <TouchableOpacity
            onPress={handleSearch}
            style={{
              padding: 12,
              borderRadius: 8,
              backgroundColor: '#2563eb',
              marginRight: 4,
            }}
          >
            <Ionicons name="search" size={24} color="white" />
          </TouchableOpacity>
        </View>
      )}

      {/* 实时预览区域 */}
      {(isRecording || isProcessingImage) && (
        <View style={{
          padding: 16,
          borderRadius: 16,
          backgroundColor: isDarkMode ? '#a9b6d0' : '#f8fafc',
          marginBottom: 24,
          alignItems: 'center',
        }}>
          <ActivityIndicator size="large" color="#2563eb" style={{marginBottom: 16}} />
          <Text style={{
            fontSize: 16,
            color: isDarkMode ? 'white' : '#1f2937',
          }}>
            {isRecording ? '正在识别您的语音内容...' : '正在分析食物图片...'}
          </Text>
        </View>
      )}

      {recognizedFood && (
        <View style={{
          padding: 20,
          borderRadius: 16,
          backgroundColor: isDarkMode ? '#a9b6d0' : '#f8fafc',
          marginBottom: 24,
          borderWidth: 1,
          borderColor: isDarkMode ? '#8fa0c1' : '#e5e7eb',
        }}>
          <View style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}>
            <Text style={{
              fontSize: 20,
              fontWeight: 'bold',
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              识别结果
            </Text>
            <TouchableOpacity onPress={resetRecognition}>
              <Ionicons name="refresh" size={22} color={isDarkMode ? 'white' : '#4b5563'} />
            </TouchableOpacity>
          </View>

          <View style={{
            flexDirection: 'row',
            marginBottom: 16,
            alignItems: 'center',
          }}>
            <View style={{
              width: 60,
              height: 60,
              borderRadius: 8,
              backgroundColor: '#f59e0b',
              justifyContent: 'center',
              alignItems: 'center',
              marginRight: 16,
            }}>
              <Ionicons name="restaurant" size={32} color="white" />
            </View>
            <View>
              <Text style={{
                fontSize: 20,
                fontWeight: 'bold',
                color: isDarkMode ? 'white' : '#1f2937',
              }}>
                {recognizedFood.name}
              </Text>
              <Text style={{
                fontSize: 15,
                color: isDarkMode ? '#e0e0e0' : '#6b7280',
              }}>
                {recognizedFood.weight}克
              </Text>
            </View>
          </View>

          <View style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}>
            {/* 热量 */}
            <View style={{
              width: '48%',
              padding: 12,
              backgroundColor: isDarkMode ? '#7e8db0' : '#f1f5f9',
              borderRadius: 12,
              marginBottom: 12,
            }}>
              <Text style={{
                fontSize: 14,
                color: isDarkMode ? '#e0e0e0' : '#6b7280',
                marginBottom: 4,
              }}>
                热量
              </Text>
              <Text style={{
                fontSize: 18,
                fontWeight: 'bold',
                color: isDarkMode ? 'white' : '#1f2937',
              }}>
                {recognizedFood.calories} 千卡
              </Text>
            </View>

            {/* 蛋白质 */}
            <View style={{
              width: '48%',
              padding: 12,
              backgroundColor: isDarkMode ? '#7e8db0' : '#f1f5f9',
              borderRadius: 12,
              marginBottom: 12,
            }}>
              <Text style={{
                fontSize: 14,
                color: isDarkMode ? '#e0e0e0' : '#6b7280',
                marginBottom: 4,
              }}>
                蛋白质
              </Text>
              <Text style={{
                fontSize: 18,
                fontWeight: 'bold',
                color: isDarkMode ? 'white' : '#1f2937',
              }}>
                {recognizedFood.protein} 克
              </Text>
            </View>

            {/* 脂肪 */}
            <View style={{
              width: '48%',
              padding: 12,
              backgroundColor: isDarkMode ? '#7e8db0' : '#f1f5f9',
              borderRadius: 12,
            }}>
              <Text style={{
                fontSize: 14,
                color: isDarkMode ? '#e0e0e0' : '#6b7280',
                marginBottom: 4,
              }}>
                脂肪
              </Text>
              <Text style={{
                fontSize: 18,
                fontWeight: 'bold',
                color: isDarkMode ? 'white' : '#1f2937',
              }}>
                {recognizedFood.fat} 克
              </Text>
            </View>

            {/* 碳水化合物 */}
            <View style={{
              width: '48%',
              padding: 12,
              backgroundColor: isDarkMode ? '#7e8db0' : '#f1f5f9',
              borderRadius: 12,
            }}>
              <Text style={{
                fontSize: 14,
                color: isDarkMode ? '#e0e0e0' : '#6b7280',
                marginBottom: 4,
              }}>
                碳水化合物
              </Text>
              <Text style={{
                fontSize: 18,
                fontWeight: 'bold',
                color: isDarkMode ? 'white' : '#1f2937',
              }}>
                {recognizedFood.carbs} 克
              </Text>
            </View>
          </View>

          <TouchableOpacity
            style={{
              padding: 16,
              borderRadius: 12,
              backgroundColor: '#2563eb',
              alignItems: 'center',
            }}
            onPress={handleConfirm}
          >
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              color: 'white',
            }}>
              确认并继续
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* 使用说明 */}
      <View style={{
        padding: 16,
        borderRadius: 16,
        backgroundColor: isDarkMode ? '#a9b6d0' : '#f8fafc',
        marginBottom: 24,
      }}>
        <Text style={{
          fontSize: 18,
          fontWeight: 'bold',
          color: isDarkMode ? 'white' : '#1f2937',
          marginBottom: 12,
        }}>
          使用说明
        </Text>
        <View style={{marginBottom: 8, flexDirection: 'row', alignItems: 'center'}}>
          <Ionicons name="mic" size={18} color={isDarkMode ? '#e0e0e0' : '#4b5563'} style={{marginRight: 8}} />
          <Text style={{
            fontSize: 14,
            color: isDarkMode ? '#e0e0e0' : '#6b7280',
          }}>
            语音识别：点击开始，说出食物名称和数量
          </Text>
        </View>
        <View style={{marginBottom: 8, flexDirection: 'row', alignItems: 'center'}}>
          <Ionicons name="camera" size={18} color={isDarkMode ? '#e0e0e0' : '#4b5563'} style={{marginRight: 8}} />
          <Text style={{
            fontSize: 14,
            color: isDarkMode ? '#e0e0e0' : '#6b7280',
          }}>
            图片识别：拍照或从相册选择食物图片
          </Text>
        </View>
        <View style={{flexDirection: 'row', alignItems: 'center'}}>
          <Ionicons name="search" size={18} color={isDarkMode ? '#e0e0e0' : '#4b5563'} style={{marginRight: 8}} />
          <Text style={{
            fontSize: 14,
            color: isDarkMode ? '#e0e0e0' : '#6b7280',
          }}>
            文字搜索：输入食物名称直接搜索
          </Text>
        </View>
      </View>
    </ScrollView>
  );
};

export default FoodInputHomeScreen; 